/**
 * Format additive probe outcome fields for Gate 1 API surface display.
 * Does not invent values — only renders what the report already contains.
 */

function labelPassFail(value) {
  const v = String(value || "").toLowerCase();
  if (v === "passed" || v === "pass") return { text: "PASS", tone: "pass" };
  if (v === "failed" || v === "fail") return { text: "FAIL", tone: "fail" };
  return null;
}

/**
 * @param {object} probeResult
 * @returns {Array<{ key: string, title: string, text: string, tone: "pass" | "fail" }>}
 */
export function probeOutcomeDisplayItems(probeResult) {
  if (!probeResult || typeof probeResult !== "object") return [];
  const items = [];

  const connection = labelPassFail(probeResult.connection);
  if (connection) {
    items.push({ key: "connection", title: "Connection", ...connection });
  }

  const operation = labelPassFail(probeResult.operation);
  if (operation) {
    items.push({ key: "operation", title: "Operation", ...operation });
  }

  if (probeResult.response && typeof probeResult.response === "object") {
    const valid = probeResult.response.valid;
    if (valid === true) {
      items.push({ key: "response", title: "Response", text: "VALID", tone: "pass" });
    } else if (valid === false) {
      items.push({ key: "response", title: "Response", text: "INVALID", tone: "fail" });
    }
  }

  return items;
}

/**
 * True when an endpoint result should offer Retry.
 * HTTP 2xx keeps "Test this Endpoint", including a blocking 200.
 * Unreachable, timeout, and HTTP ≥ 400 still offer Retry.
 * @param {object} probeResult
 */
export function probeResultFailed(probeResult) {
  if (!probeResult || typeof probeResult !== "object") return false;
  const status = probeResult.status_code != null ? Number(probeResult.status_code) : null;
  if (status != null && status >= 200 && status < 300) return false;
  if (probeResult.reachable === false || probeResult.blocking === true) return true;
  return status != null && status >= 400;
}

/**
 * Retry metadata from the backend (attempt count + classification hint), or null.
 * @param {object} probeResult
 * @returns {{ attemptsText: string | null, hint: string | null } | null}
 */
export function probeRetryNote(probeResult) {
  if (!probeResult || typeof probeResult !== "object") return null;
  const attempts = Number(probeResult.attempts);
  const attemptsText = Number.isFinite(attempts) && attempts > 1 ? `${attempts} attempts` : null;
  const hint =
    typeof probeResult.failure_hint === "string" && probeResult.failure_hint.trim()
      ? probeResult.failure_hint.trim()
      : null;
  if (!attemptsText && !hint) return null;
  return { attemptsText, hint };
}
